import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Clock,
  Flag,
  HelpCircle,
  Search,
  Trophy,
  Users,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { AdPlacement } from "@/components/ads/AdPlacement";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { m } from "@/paraglide/messages";
import { F1Explainer } from "../components/F1Explainer";
import { SeasonRaceSelector } from "../components/SeasonRaceSelector";
import { StandingsSkeleton } from "../components/StandingsSkeletons";
import {
  type ConstructorStanding,
  type DriverStanding,
  useConstructorStandings,
  useDriverStandings,
  useRaces,
} from "../hooks/use-f1-data";
import { DEFAULT_SEASON } from "../lib/constants";
import { getTeamColor } from "../lib/team-colors";

// Route API for accessing loader data and search params
const routeApi = getRouteApi("/f1-fantasy/standings");

function getContrastTextColor(hexColor: string): string {
  const r = parseInt(hexColor.slice(1, 3), 16) / 255;
  const g = parseInt(hexColor.slice(3, 5), 16) / 255;
  const b = parseInt(hexColor.slice(5, 7), 16) / 255;
  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return L > 0.179 ? "#111" : "#fff";
}

export function Standings() {
  // Get SSR-prefetched loader data for initial state
  const loaderData = routeApi.useLoaderData();
  const navigate = useNavigate({ from: "/f1-fantasy/standings" });
  const { tab = "drivers" } = routeApi.useSearch();

  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<number>(
    loaderData?.season ?? DEFAULT_SEASON,
  );

  // Fetch standings for both tabs (driver standings SSR prefetched)
  const { data: races } = useRaces(selectedSeason);
  // Capture current time once at mount — stable reference for filtering past races
  const [now] = useState(() => Date.now());
  const lastRace = useMemo(() => {
    if (!races) return null;
    const past = races.filter(
      (r) => r.date && new Date(r.date).getTime() < now,
    );
    return past.length > 0 ? past[past.length - 1] : null;
  }, [races, now]);

  const { data: driverStandings, isLoading: driversLoading } =
    useDriverStandings(selectedSeason);
  const { data: constructorStandings, isLoading: constructorsLoading } =
    useConstructorStandings(selectedSeason);

  // Determine which data to show based on tab
  const isDriversTab = tab === "drivers";
  const currentStandings = isDriversTab
    ? driverStandings
    : constructorStandings;
  const isLoading = isDriversTab ? driversLoading : constructorsLoading;
  const showLoading = isLoading && !currentStandings;

  // Filter standings based on search
  const filteredStandings = useMemo(() => {
    if (!currentStandings) return [];

    return currentStandings.filter((standing) => {
      const searchLower = searchQuery.toLowerCase();

      if (isDriversTab) {
        const driverStanding = standing as DriverStanding;
        return (
          driverStanding.driverFullName?.toLowerCase().includes(searchLower) ||
          driverStanding.constructorName?.toLowerCase().includes(searchLower)
        );
      }

      const constructorStanding = standing as ConstructorStanding;
      return constructorStanding.constructorName
        ?.toLowerCase()
        .includes(searchLower);
    });
  }, [currentStandings, searchQuery, isDriversTab]);

  // Split into podium (top 3) and rest (4+)
  // When searching, show all results in the table (no podium split)
  const isSearching = searchQuery.trim().length > 0;
  const podiumStandings = isSearching ? [] : filteredStandings.slice(0, 3);
  const tableStandings = isSearching
    ? filteredStandings
    : filteredStandings.slice(3);

  // Championship leader's points (P1, unfiltered) — drives the gap column.
  const leaderPoints = currentStandings?.[0]?.points ?? 0;

  const handleTabChange = (newTab: "drivers" | "constructors") => {
    navigate({ search: { tab: newTab } });
  };

  return (
    <div>
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4 space-y-3 md:space-y-0">
          {/* Row 1: title + (desktop: controls) */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/f1-fantasy"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">{m.common_back()}</span>
              </Link>
              <div className="w-px h-6 bg-border" />
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                <h1 className="font-display text-xl font-bold text-foreground">
                  {m.f1_standings_page_title()}
                </h1>
              </div>
            </div>

            {/* Mobile search icon — hidden on desktop */}
            <button
              type="button"
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              aria-label={
                mobileSearchOpen
                  ? m.common_close_search()
                  : m.common_open_search()
              }
              aria-expanded={mobileSearchOpen}
              className={`md:hidden p-2 rounded-lg transition-colors ${
                mobileSearchOpen
                  ? "bg-primary/20 border border-primary/50 text-primary"
                  : "chip-idle text-foreground/70"
              }`}
            >
              <Search className="h-4 w-4" aria-hidden="true" />
            </button>

            {/* Desktop controls — hidden on mobile */}
            <div className="hidden md:flex items-center gap-4 flex-wrap">
              {/* Tab Selector */}
              <div className="flex rounded-lg tab-group p-0.5">
                <button
                  type="button"
                  onClick={() => handleTabChange("drivers")}
                  className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                    isDriversTab
                      ? "chip-on"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  <Users className="h-4 w-4" />
                  {m.f1_standings_tab_drivers()}
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange("constructors")}
                  className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                    !isDriversTab
                      ? "chip-on"
                      : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  <Flag className="h-4 w-4" />
                  {m.f1_standings_tab_constructors()}
                </button>
              </div>

              {/* Season Selector */}
              <SeasonRaceSelector
                key={`desktop-${selectedSeason}`}
                defaultSeason={selectedSeason}
                onSeasonChange={setSelectedSeason}
                showRaceSelector={false}
              />

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={
                    isDriversTab
                      ? m.f1_standings_search_drivers()
                      : m.f1_standings_search_constructors()
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 w-64 rounded-lg bg-muted/50 border border-border text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
                />
              </div>
            </div>
          </div>

          {/* Mobile controls — stacked rows */}
          <div className="md:hidden space-y-2">
            {/* Expandable search — immediately below title row */}
            {mobileSearchOpen && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={
                    isDriversTab
                      ? m.f1_standings_search_drivers()
                      : m.f1_standings_search_constructors()
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 rounded-lg bg-muted/50 border border-border text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label={m.common_clear_search()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            {/* Year selector */}
            <SeasonRaceSelector
              key={`mobile-${selectedSeason}`}
              defaultSeason={selectedSeason}
              onSeasonChange={setSelectedSeason}
              showRaceSelector={false}
              compact
            />

            {/* Tab Selector */}
            <div className="flex rounded-lg tab-group p-0.5">
              <button
                type="button"
                onClick={() => handleTabChange("drivers")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md transition-all ${
                  isDriversTab
                    ? "chip-on"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                <Users className="h-4 w-4" />
                {m.f1_standings_tab_drivers()}
              </button>
              <button
                type="button"
                onClick={() => handleTabChange("constructors")}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md transition-all ${
                  !isDriversTab
                    ? "chip-on"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                <Flag className="h-4 w-4" />
                {m.f1_standings_tab_constructors()}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Intro + Data Freshness */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {m.f1_standings_intro({ season: selectedSeason })}
          </p>
          {lastRace && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              {m.f1_standings_data_through({ name: lastRace.name })}
            </p>
          )}
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors mt-2"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                {m.f1_standings_how_it_works()}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{m.f1_standings_how_title()}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm text-foreground/80">
                <p>{m.f1_standings_how_p1()}</p>
                <p>{m.f1_standings_how_p2()}</p>
                <p>{m.f1_standings_how_p3()}</p>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <AdPlacement className="my-6" />

        {/* Loading State */}
        {showLoading && <StandingsSkeleton showTeamColumn={isDriversTab} />}

        {/* ============================================================
				    MOBILE LAYOUT (below md:) — vertical podium + cards
				    ============================================================ */}
        <div className="md:hidden">
          {/* Mobile Podium */}
          {!showLoading && podiumStandings.length >= 3 && (
            <MobilePodium
              standings={podiumStandings}
              isDriversTab={isDriversTab}
            />
          )}

          {/* Mobile Cards for 4th+ */}
          {!showLoading && tableStandings.length > 0 && (
            <div className="space-y-2 mt-4">
              {tableStandings.map((standing) => (
                <MobileStandingCard
                  key={standing.id}
                  standing={standing}
                  isDriversTab={isDriversTab}
                  leaderPoints={leaderPoints}
                />
              ))}
            </div>
          )}
        </div>

        <AdPlacement className="my-6" />

        {/* ============================================================
				    DESKTOP LAYOUT (md: and above) — unchanged
				    ============================================================ */}
        <div className="hidden md:block">
          {/* Podium (Top 3) */}
          {!showLoading && podiumStandings.length >= 3 && (
            <Podium standings={podiumStandings} isDriversTab={isDriversTab} />
          )}

          {/* Table (4th place onwards) */}
          {!showLoading && tableStandings.length > 0 && (
            <StandingsTable
              standings={tableStandings}
              isDriversTab={isDriversTab}
              leaderPoints={leaderPoints}
            />
          )}
        </div>

        {/* Shared: Empty state + season info */}
        {!showLoading && filteredStandings.length === 0 && (
          <div className="text-center py-16">
            {isDriversTab ? (
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            ) : (
              <Flag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            )}
            <p className="text-muted-foreground">
              {searchQuery
                ? isDriversTab
                  ? m.f1_standings_no_drivers_search()
                  : m.f1_standings_no_constructors_search()
                : isDriversTab
                  ? m.f1_standings_no_driver_data()
                  : m.f1_standings_no_constructor_data()}
            </p>
          </div>
        )}

        {!showLoading && filteredStandings.length > 0 && (
          <div className="mt-8 text-center text-sm text-muted-foreground">
            {m.f1_standings_showing({ season: selectedSeason })}
          </div>
        )}
      </div>
    </div>
  );
}

// Podium component for top 3 (2-1-3 visual layout)
function Podium({
  standings,
  isDriversTab,
}: {
  standings: (DriverStanding | ConstructorStanding)[];
  isDriversTab: boolean;
}) {
  if (standings.length < 3) return null;

  const positionColors: Record<number, string> = {
    1: "from-yellow-400 to-yellow-600",
    2: "from-gray-300 to-gray-500",
    3: "from-amber-600 to-amber-800",
  };

  // Get data for each position
  const first = standings[0];
  const second = standings[1];
  const third = standings[2];

  const getPodiumCard = (
    standing: DriverStanding | ConstructorStanding,
    size: "lg" | "md" | "sm",
  ) => {
    const teamSlug = isDriversTab
      ? (standing as DriverStanding).constructorSlug
      : (standing as ConstructorStanding).constructorSlug;
    const teamColor = getTeamColor(teamSlug);
    const name = isDriversTab
      ? (standing as DriverStanding).driverFullName
      : (standing as ConstructorStanding).constructorName;
    const team = isDriversTab
      ? (standing as DriverStanding).constructorName
      : null;

    const sizeClasses = {
      lg: "p-6",
      md: "p-5",
      sm: "p-4",
    };

    const textSizes = {
      lg: { name: "text-xl", points: "text-3xl", badge: "text-2xl" },
      md: { name: "text-lg", points: "text-2xl", badge: "text-xl" },
      sm: { name: "text-base", points: "text-xl", badge: "text-lg" },
    };

    return (
      <div
        className={`rounded-xl border-2 overflow-hidden transition-transform hover:scale-[1.02] ${sizeClasses[size]}`}
        style={{
          borderColor: teamColor,
          backgroundColor: `${teamColor}10`,
        }}
      >
        {/* Position Badge */}
        <div
          className={`inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gradient-to-br ${positionColors[standing.position]} mb-4`}
        >
          <span
            className={`${textSizes[size].badge} font-display font-bold text-white drop-shadow`}
          >
            P{standing.position}
          </span>
        </div>

        {/* Name */}
        <h3
          className={`${textSizes[size].name} font-display font-semibold mb-1`}
        >
          {name}
        </h3>

        {/* Team (drivers only) */}
        {team && <p className="text-sm text-muted-foreground mb-4">{team}</p>}

        {/* Points */}
        <div className="mt-auto">
          <div
            className={`${textSizes[size].points} font-mono font-bold text-primary`}
          >
            {standing.points}
          </div>
          <div className="text-xs text-muted-foreground">
            {m.f1_standings_col_points()}
          </div>
        </div>

        {/* Wins & Podiums */}
        <div className="flex gap-4 mt-3 pt-3 border-t border-border/50">
          <div className="flex-1 text-center">
            <div className="font-mono font-bold text-foreground">
              {standing.wins}
            </div>
            <div className="text-xs text-muted-foreground">
              {m.f1_standings_col_wins()}
            </div>
          </div>
          <div className="flex-1 text-center">
            <div className="font-mono font-bold text-foreground">
              {standing.podiums}
            </div>
            <div className="text-xs text-muted-foreground">
              {m.f1_standings_col_podiums()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-display font-bold mb-6 text-center">
        {m.f1_standings_top3()}
      </h2>
      {/* 2-1-3 Layout: P2 (left), P1 (center, larger), P3 (right) */}
      <div className="grid grid-cols-3 gap-4 max-w-4xl mx-auto items-end">
        {/* P2 - Second Place */}
        <div className="flex flex-col justify-end">
          {getPodiumCard(second, "md")}
        </div>

        {/* P1 - First Place (larger) */}
        <div className="flex flex-col justify-end">
          {getPodiumCard(first, "lg")}
        </div>

        {/* P3 - Third Place */}
        <div className="flex flex-col justify-end">
          {getPodiumCard(third, "sm")}
        </div>
      </div>
    </div>
  );
}

// Mobile Podium — vertical stack for top 3
function MobilePodium({
  standings,
  isDriversTab,
}: {
  standings: (DriverStanding | ConstructorStanding)[];
  isDriversTab: boolean;
}) {
  if (standings.length < 3) return null;

  const medals = ["\u{1F947}", "\u{1F948}", "\u{1F949}"];
  const accentColors = [
    "border-yellow-500/60",
    "border-gray-400/60",
    "border-amber-600/60",
  ];

  return (
    <div className="space-y-2">
      <h2 className="text-lg font-display font-bold text-center mb-3">
        {m.f1_standings_top3()}
      </h2>
      {standings.slice(0, 3).map((standing, idx) => {
        const teamSlug = isDriversTab
          ? (standing as DriverStanding).constructorSlug
          : (standing as ConstructorStanding).constructorSlug;
        const teamColor = getTeamColor(teamSlug);
        const name = isDriversTab
          ? (standing as DriverStanding).driverFullName
          : (standing as ConstructorStanding).constructorName;
        const team = isDriversTab
          ? (standing as DriverStanding).constructorName
          : null;
        const isFirst = idx === 0;

        return (
          <div
            key={standing.id}
            className={`rounded-xl border-2 overflow-hidden ${accentColors[idx]} ${isFirst ? "p-4" : "p-3"}`}
            style={{ backgroundColor: `${teamColor}08` }}
          >
            <div className="flex items-center gap-3">
              <span className={isFirst ? "text-2xl" : "text-xl"}>
                {medals[idx]}
              </span>
              <div className="flex-1 min-w-0">
                <div
                  className={`font-display font-semibold truncate ${isFirst ? "text-lg" : "text-base"}`}
                >
                  {name}
                </div>
                {team && (
                  <div className="text-xs text-muted-foreground">{team}</div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div
                  className={`font-mono font-bold text-primary ${isFirst ? "text-2xl" : "text-xl"}`}
                >
                  {standing.points}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {m.f1_standings_pts_label()}
                </div>
              </div>
            </div>
            {isFirst && (
              <div className="flex gap-4 mt-2 pt-2 border-t border-border/30">
                <span className="text-xs text-muted-foreground">
                  <span className="font-mono font-bold text-foreground">
                    {standing.wins}
                  </span>{" "}
                  {m.f1_standings_col_wins()}
                </span>
                <span className="text-xs text-muted-foreground">
                  <span className="font-mono font-bold text-foreground">
                    {standing.podiums}
                  </span>{" "}
                  {m.f1_standings_col_podiums()}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Mobile Standing Card — for 4th+ positions
function MobileStandingCard({
  standing,
  isDriversTab,
  leaderPoints,
}: {
  standing: DriverStanding | ConstructorStanding;
  isDriversTab: boolean;
  leaderPoints: number;
}) {
  const teamSlug = isDriversTab
    ? (standing as DriverStanding).constructorSlug
    : (standing as ConstructorStanding).constructorSlug;
  const teamColor = getTeamColor(teamSlug);
  const name = isDriversTab
    ? (standing as DriverStanding).driverFullName
    : (standing as ConstructorStanding).constructorName;
  const team = isDriversTab
    ? (standing as DriverStanding).constructorName
    : null;
  const number = isDriversTab
    ? (standing as DriverStanding).driverPermanentNumber
    : null;
  const nationality = isDriversTab
    ? (standing as DriverStanding).driverNationality
    : null;
  const gap = leaderPoints - standing.points;

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-card/50">
      {/* Team color bar */}
      <div
        className="w-1 h-10 rounded-full shrink-0"
        style={{ backgroundColor: teamColor }}
      />

      {/* Position */}
      <span className="font-mono font-bold text-sm w-6 text-center shrink-0">
        {standing.position}
      </span>

      {/* Name (+ number) + team · nationality */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-sm truncate">{name}</span>
          {number != null && (
            <span className="font-mono text-[10px] text-muted-foreground shrink-0">
              #{number}
            </span>
          )}
        </div>
        {(team || nationality) && (
          <div className="text-xs text-muted-foreground truncate">
            {[team, nationality].filter(Boolean).join(" · ")}
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {standing.wins > 0 && (
            <span className="rounded bg-muted/60 px-1.5 py-0.5 text-[11px] font-medium text-foreground/75">
              {m.f1_standings_wins_short({ count: standing.wins })}
            </span>
          )}
          {standing.podiums > 0 && (
            <span className="rounded bg-muted/60 px-1.5 py-0.5 text-[11px] font-medium text-foreground/75">
              {m.f1_standings_podiums_short({ count: standing.podiums })}
            </span>
          )}
          {gap > 0 && (
            <span className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] text-foreground/75">
              −{gap}
            </span>
          )}
        </div>
      </div>

      {/* Points */}
      <div className="font-mono font-bold text-primary text-sm shrink-0">
        {standing.points}
      </div>
    </div>
  );
}

// Table component for positions 4+
function StandingsTable({
  standings,
  isDriversTab,
  leaderPoints,
}: {
  standings: (DriverStanding | ConstructorStanding)[];
  isDriversTab: boolean;
  leaderPoints: number;
}) {
  if (standings.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground w-16">
                {m.f1_standings_col_pos()}
              </th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">
                {isDriversTab
                  ? m.f1_standings_tab_drivers()
                  : m.f1_standings_tab_constructors()}
              </th>
              {isDriversTab && (
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">
                  {m.f1_standings_col_team()}
                </th>
              )}
              {isDriversTab && (
                <th className="text-left px-4 py-3 text-sm font-semibold text-muted-foreground">
                  {m.f1_standings_col_nationality()}
                </th>
              )}
              <th className="text-center px-4 py-3 text-sm font-semibold text-muted-foreground w-16">
                {m.f1_standings_col_wins()}
              </th>
              <th className="text-center px-4 py-3 text-sm font-semibold text-muted-foreground w-20">
                {m.f1_standings_col_podiums()}
              </th>
              <th className="text-right px-4 py-3 text-sm font-semibold text-muted-foreground w-24">
                {m.f1_standings_col_points()}
              </th>
              <th className="text-right px-4 py-3 text-sm font-semibold text-muted-foreground w-20">
                {m.f1_standings_col_gap()}
              </th>
            </tr>
          </thead>
          <tbody>
            {standings.map((standing) => {
              const teamSlug = isDriversTab
                ? (standing as DriverStanding).constructorSlug
                : (standing as ConstructorStanding).constructorSlug;
              const teamColor = getTeamColor(teamSlug);
              const name = isDriversTab
                ? (standing as DriverStanding).driverFullName
                : (standing as ConstructorStanding).constructorName;
              const driverNumber = isDriversTab
                ? (standing as DriverStanding).driverPermanentNumber
                : null;
              const driverNationality = isDriversTab
                ? (standing as DriverStanding).driverNationality
                : null;

              return (
                <tr
                  key={standing.id}
                  className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                >
                  {/* Position */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-1 h-6 rounded"
                        style={{ backgroundColor: teamColor }}
                      />
                      <span className="font-mono font-medium">
                        {standing.position}
                      </span>
                    </div>
                  </td>

                  {/* Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-semibold">{name}</div>
                        {driverNumber && (
                          <span
                            className="inline-block px-2 py-0.5 rounded text-xs font-mono font-bold mt-1"
                            style={{
                              backgroundColor: teamColor,
                              color: getContrastTextColor(teamColor),
                            }}
                          >
                            #{driverNumber}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Team (drivers only) */}
                  {isDriversTab && (
                    <td className="px-4 py-3 text-muted-foreground">
                      {(standing as DriverStanding).constructorName}
                    </td>
                  )}

                  {/* Nationality (drivers only) */}
                  {isDriversTab && (
                    <td className="px-4 py-3 text-muted-foreground">
                      {driverNationality ?? "\u2014"}
                    </td>
                  )}

                  {/* Wins */}
                  <td className="px-4 py-3 text-center">
                    <span className="font-mono font-medium">
                      {standing.wins > 0 ? standing.wins : "\u2014"}
                    </span>
                  </td>

                  {/* Podiums */}
                  <td className="px-4 py-3 text-center">
                    <span className="font-mono font-medium">
                      {standing.podiums > 0 ? standing.podiums : "\u2014"}
                    </span>
                  </td>

                  {/* Points */}
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono font-bold text-primary text-lg">
                      {standing.points}
                    </span>
                  </td>

                  {/* Gap to leader */}
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono text-sm text-muted-foreground">
                      {leaderPoints > standing.points
                        ? `−${leaderPoints - standing.points}`
                        : "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <F1Explainer
        title={m.f1_st_title()}
        howTo={m.f1_st_howto()}
        faqs={[
          { q: m.f1_st_faq1_q(), a: m.f1_st_faq1_a() },
          { q: m.f1_st_faq2_q(), a: m.f1_st_faq2_a() },
          { q: m.f1_st_faq3_q(), a: m.f1_st_faq3_a() },
          { q: m.f1_st_faq4_q(), a: m.f1_st_faq4_a() },
        ]}
      />
      <AdPlacement className="mt-6" />
    </div>
  );
}
